// OrderBilling API
import queryString from 'query-string'
import {
    RequestConfigFunction,
} from 'app/types/request.types'

type OrderBillingRequests = {
    domainName: string,
    getAdditionalFees: RequestConfigFunction<{outgoingInvoiceId: number}>,
    updateAdditionalFees: RequestConfigFunction<{orderNumber: string, data: Record<string, any>}>,
    filterApplicableAdditionalFees: RequestConfigFunction<{orderNumber: string}>,
    getAllowedProductTypes: RequestConfigFunction<{orderNumber: string}>,
    deleteOutgoingInvoice: RequestConfigFunction<{orderNumber: string, outgoingInvoiceId: number}>,
    updateInvoiceStatus: RequestConfigFunction<{
        outgoingInvoiceId: number, data: Record<string, any>
    }>,
    getInvoice: RequestConfigFunction<{outgoingInvoiceId: number}>,
    filterAllOutgoingInvoices: RequestConfigFunction<{orderNumber: string}>,
    filterAllPositions: RequestConfigFunction<{outgoingInvoiceId: number}>,
    countOrdersWithAdditionalFee: RequestConfigFunction<{id: number, data: {
            validTo: string, validFrom: string,
        }}>,
    countOrdersWithLaneFee: RequestConfigFunction<{id: number, data: Record<string, any>}>,
    filterApplicableLaneFees: RequestConfigFunction<{orderNumber: string}>,
    updateBillingInformation: RequestConfigFunction<{orderNumber: string}>,
    updatePosition: RequestConfigFunction<{outgoingInvoiceId: number, positionId: number}>,
    deletePosition: RequestConfigFunction<{outgoingInvoiceId: number, positionId: number}>,
    updateGeneralInformation: RequestConfigFunction<{
        outgoingInvoiceId: number,
        data?: Record<string, any>
    }>,
    createCreditNoteInvoice: RequestConfigFunction<{ data: Record<string, any> }>,
    getPositionPrePopulation: RequestConfigFunction<{
        outgoingInvoiceId: number, positionCode: string
    }>,
    createPosition: RequestConfigFunction<{outgoingInvoiceId: number}>,
    getLaneFees: RequestConfigFunction<{outgoingInvoiceId: string}>,
    updateLaneFees: RequestConfigFunction<{orderNumber: string, data?: Record<string, any>}>,
    createOutgoingInvoice: RequestConfigFunction<{orderNumber: string, data: Record<string, any>}>,
    getPositionTypeMapping: RequestConfigFunction<undefined>,
    sendToSap: RequestConfigFunction<{ outgoingInvoiceId: number }>,
}

const orderBillingRequests: OrderBillingRequests = {
    domainName: 'OrderBilling',
    getAdditionalFees: ({
        outgoingInvoiceId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/additionalfees`,
            method: 'GET',
        }
    },
    updateAdditionalFees: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/billing/additionalfees`,
            method: 'PATCH',
            data,
        }
    },
    filterApplicableAdditionalFees: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/billing/additionalfees/filter-request/applicable`,
            method: 'POST',
        }
    },
    getAllowedProductTypes: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/billing/allowedproducttypes`,
            method: 'GET',
        }
    },
    deleteOutgoingInvoice: ({
        orderNumber, outgoingInvoiceId,
    }) => {
        return {
            url: `order/${orderNumber}/billing/outgoinginvoice/${outgoingInvoiceId}`,
            method: 'DELETE',
        }
    },
    updateInvoiceStatus: ({
        outgoingInvoiceId,
        data,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/invoice-status`,
            method: 'PATCH',
            data,
        }
    },
    getInvoice: ({
        outgoingInvoiceId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}`,
            method: 'GET',
        }
    },
    filterAllOutgoingInvoices: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/billing/outgoinginvoice/filter-request/all`,
            method: 'POST',
            data: {
                rows: 50,
                start: 0,
            },
        }
    },
    filterAllPositions: ({
        outgoingInvoiceId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/position/filter-request/all`,
            method: 'POST',
        }
    },
    countOrdersWithAdditionalFee: ({
        id,
        data,
    }) => {
        return {
            url: `order/billing/count/additionalfees/${id}`,
            method: 'POST',
            data,
        }
    },
    countOrdersWithLaneFee: ({
        id,
        data,
    }) => {
        return {
            url: `order/billing/count/lanefees/${id}`,
            method: 'POST',
            data,
        }
    },
    filterApplicableLaneFees: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/billing/lanefees/filter-request/applicable`,
            method: 'POST',
        }
    },
    updateBillingInformation: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/billing/billing-information`,
            method: 'PATCH',
        }
    },
    updatePosition: ({
        outgoingInvoiceId, positionId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/position/${positionId}`,
            method: 'PUT',
        }
    },
    deletePosition: ({
        outgoingInvoiceId, positionId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/position/${positionId}`,
            method: 'DELETE',
        }
    },
    updateGeneralInformation: ({
        outgoingInvoiceId,
        data,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/general-information`,
            method: 'PATCH',
            data,
        }
    },
    createCreditNoteInvoice: ({
        data,
    }) => {
        return {
            url: 'outgoinginvoice/creditnoteinvoice',
            method: 'POST',
            data,
        }
    },
    getPositionPrePopulation: ({
        outgoingInvoiceId,
        positionCode,
    }) => {
        return {
            url: queryString.stringifyUrl({
                url: `outgoinginvoice/${outgoingInvoiceId}/position/pre-population`,
                query: {
                    positionCode,
                },
            }),
            method: 'GET',
        }
    },
    createPosition: ({
        outgoingInvoiceId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/position`,
            method: 'POST',
        }
    },
    getLaneFees: ({
        outgoingInvoiceId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/lanefees`,
            method: 'GET',
        }
    },
    updateLaneFees: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/billing/lanefees`,
            method: 'PATCH',
            data,
        }
    },
    createOutgoingInvoice: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/billing/outgoinginvoice`,
            method: 'POST',
            data,
        }
    },
    getPositionTypeMapping: () => {
        return {
            url: 'outgoinginvoice/positiontype/mapping',
            method: 'GET',
        }
    },
    sendToSap: ({
        outgoingInvoiceId,
    }) => {
        return {
            url: `outgoinginvoice/${outgoingInvoiceId}/sap-sending`,
            method: 'PATCH',
        }
    },
}

export default orderBillingRequests
